import { useMemo, useState } from 'react';
import { findPov, type ShotMark } from '@studio/shared';

import { api, assetUrl, downloadUrl, type GalleryEntry, type InsightCut, type Insights } from '../lib/api';
import { DownloadIcon } from '../components/Icons';
import { Display } from '../components/Display';
import { SortDeck } from '../components/SortDeck';
import { MarkStrip } from '../components/MarkStrip';
import { Sheet } from '../components/Sheet';
import { Skeleton } from '../components/Skeleton';
import { useToast } from '../components/Toaster';
import { usePresetLabel, useT } from '../lib/i18n';
import { useDateFormat } from '../lib/dates';
import { useLoad } from '../lib/useLoad';

type View = 'all' | 'keep' | 'unmarked';

const VIEWS: Array<{ id: View; key: string }> = [
  { id: 'all', key: 'sheet.view.all' },
  { id: 'keep', key: 'sheet.view.keep' },
  { id: 'unmarked', key: 'sheet.view.unmarked' },
];

/** Press once to circle, again to cross, again to clear. */
/**
 * Direction A: a real contact sheet, printed on paper and marked with a
 * chinagraph pencil.
 *
 * The job on this page is not browsing, it is *selecting* — you shot
 * twenty-four and need the four that ship. Circling and crossing is the
 * century-old gesture editors already know, and here the marks are data:
 * circled frames export as a set.
 */
export function GalleryPage() {
  const toast = useToast();
  const t = useT();
  const date = useDateFormat();
  const tp = usePresetLabel();
  const {
    data: entries,
    setData: setEntries,
    loading,
  } = useLoad(() => api.gallery(), {
    initial: [] as GalleryEntry[],
  });
  const [view, setView] = useState<View>('all');
  const [povFilter, setPovFilter] = useState('');
  /*
   * Whether exports go out at twice the size, resampled and sharpened. Kept
   * per browser because it follows the work — a studio shooting for print
   * wants it every time, one shooting for a web grid never does.
   */
  const [big, setBig] = useState(() => {
    try {
      return localStorage.getItem('studio.export2x') === '1';
    } catch {
      return false;
    }
  });
  const setExportSize = (next: boolean) => {
    setBig(next);
    try {
      localStorage.setItem('studio.export2x', next ? '1' : '0');
    } catch {
      // A private window simply asks again next time.
    }
  };
  const [open, setOpen] = useState<GalleryEntry | undefined>();

  const povs = useMemo(() => [...new Set(entries.map((e) => e.shot.povId))], [entries]);
  /** A set-up's label in the current language, falling back to its English name. */
  const povName = (id: string) => tp(`pov.${id}.name`, findPov(id)?.name ?? id);
  const keepers = useMemo(() => entries.filter((e) => e.shot.mark === 'keep'), [entries]);
  /*
   * How many frames each view would show, on the control that switches to it.
   * "Unjudged" is the one that matters — it is the size of the job left, and
   * without the number you find it out by pressing.
   */
  const counts = useMemo(
    () => ({
      all: entries.length,
      keep: keepers.length,
      unmarked: entries.filter((e) => !e.shot.mark).length,
    }),
    [entries, keepers],
  );

  /*
   * Quick sort first while anything is unjudged — that is the job this page
   * exists for — and the whole sheet once nothing is. Either can be chosen.
   */
  const [mode, setMode] = useState<'sort' | 'sheet' | undefined>();
  const showing = mode ?? (counts.unmarked > 0 ? 'sort' : 'sheet');
  const queue = useMemo(() => entries.filter((e) => !e.shot.mark), [entries]);
  const rejects = entries.filter((e) => e.shot.mark === 'reject').length;
  /** The last frame judged in quick sort, so one slip can be taken back. */
  const [last, setLast] = useState<string | undefined>();

  const shown = entries.filter((entry) => {
    if (povFilter && entry.shot.povId !== povFilter) return false;
    if (view === 'keep') return entry.shot.mark === 'keep';
    if (view === 'unmarked') return !entry.shot.mark;
    return true;
  });

  /* A sheet is printed per roll — that is what a contact sheet is. */
  const sheets = useMemo(() => {
    const map = new Map<string, { name: string; date: string; entries: GalleryEntry[] }>();
    for (const entry of shown) {
      const id = entry.shoot?.id ?? 'loose';
      const bucket = map.get(id) ?? {
        name: entry.shoot?.name ?? 'Loose frames',
        date: entry.shoot?.createdAt ?? entry.shot.createdAt,
        entries: [],
      };
      bucket.entries.push(entry);
      map.set(id, bucket);
    }
    return [...map.values()];
  }, [shown]);

  /*
   * Where the open frame sits in what is currently shown, so the panel can
   * move to the next one. Keyed on the shot rather than on an index, because
   * marking a frame while the "unjudged" filter is on takes it out of the
   * list under the panel — and stepping from a position that no longer exists
   * would jump somewhere arbitrary. Stepping from a missing frame starts at
   * the beginning, which is at least explicable.
   */
  const at = open ? shown.findIndex((entry) => entry.shot.id === open.shot.id) : -1;
  const stepTo = (delta: number) => {
    if (!shown.length) return;
    const from = at < 0 ? 0 : at;
    setOpen(shown[(from + delta + shown.length) % shown.length]);
  };

  /** Optimistic: the mark lands under your finger, and rolls back if refused. */
  const mark = async (shotId: string, next: ShotMark | null) => {
    const before = entries;
    setEntries((prev) =>
      prev.map((e) => (e.shot.id === shotId ? { ...e, shot: { ...e.shot, mark: next ?? undefined } } : e)),
    );
    setOpen((prev) =>
      prev && prev.shot.id === shotId ? { ...prev, shot: { ...prev.shot, mark: next ?? undefined } } : prev,
    );
    try {
      await api.markShot(shotId, next);
    } catch (err) {
      setEntries(before);
      toast(err instanceof Error ? err.message : t('roll.markError'), 'error');
    }
  };

  /*
   * Browsers throttle a burst of programmatic downloads, so the keepers go out
   * one every 350ms rather than all at once — slower, but none get dropped.
   */
  const exportKeepers = async () => {
    const files = keepers.filter((e) => e.asset);
    if (!files.length) return;
    toast(t(files.length === 1 ? 'sheet.pullingOne' : 'sheet.pulling', { n: files.length }));
    for (const entry of files) {
      const link = document.createElement('a');
      link.href = downloadUrl(entry.asset!.id, big ? '2x' : undefined);
      link.download = '';
      document.body.append(link);
      link.click();
      link.remove();
      await new Promise((resolve) => setTimeout(resolve, 350));
    }
  };

  let frameNo = 0;

  return (
    <div className="app__inner">
      <header className="pagehead">
        <Display text={t('sheet.display')} mark="none" />
        <div className="row row--wrap">
          {entries.length > 0 && (
            <div className="segmented" role="group" aria-label={t('sheet.display')}>
              <button
                type="button"
                className={showing === 'sort' ? 'is-on' : ''}
                aria-pressed={showing === 'sort'}
                onClick={() => setMode('sort')}
              >
                {t('sort.tab')}
                <b>{counts.unmarked}</b>
              </button>
              <button
                type="button"
                className={showing === 'sheet' ? 'is-on' : ''}
                aria-pressed={showing === 'sheet'}
                onClick={() => setMode('sheet')}
              >
                {t('sheet.tabSheet')}
                <b>{entries.length}</b>
              </button>
            </div>
          )}
          {keepers.length > 0 && (
            <button type="button" className="btn btn--primary" onClick={exportKeepers}>
              <DownloadIcon />{' '}
              {t(keepers.length === 1 ? 'sheet.exportOne' : 'sheet.export', { n: keepers.length })}
            </button>
          )}
        </div>
      </header>

      {showing === 'sort' && !loading && entries.length > 0 ? (
        <SortDeck
          queue={queue}
          total={entries.length}
          kept={keepers.length}
          cut={rejects}
          onMark={(shotId, next) => {
            setLast(shotId);
            void mark(shotId, next);
          }}
          onUndo={
            last
              ? () => {
                  void mark(last, null);
                  setLast(undefined);
                }
              : undefined
          }
          onOpen={setOpen}
          onDone={() => setMode('sheet')}
        />
      ) : (
        <>
          {entries.length > 0 && (
            <div className="sheet-bar">
              <div className="segmented" role="group" aria-label={t('sheet.viewLabel')}>
                {VIEWS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={view === option.id ? 'is-on' : ''}
                    aria-pressed={view === option.id}
                    onClick={() => setView(option.id)}
                  >
                    {t(option.key)}
                    <b>{counts[option.id]}</b>
                  </button>
                ))}
              </div>

              {povs.length > 1 && (
                <div className="sheet-bar__chips">
                  <button
                    type="button"
                    className={`chip ${povFilter === '' ? 'is-on' : ''}`}
                    onClick={() => setPovFilter('')}
                  >
                    {t('sheet.anySetUp')}
                  </button>
                  {povs.map((povId) => (
                    <button
                      key={povId}
                      type="button"
                      className={`chip ${povFilter === povId ? 'is-on' : ''}`}
                      onClick={() => setPovFilter(povId)}
                    >
                      {povName(povId)}
                    </button>
                  ))}
                </div>
              )}

              <div className="segmented" role="group" aria-label={t('sheet.exportSize')}>
                <button
                  type="button"
                  className={big ? '' : 'is-on'}
                  aria-pressed={!big}
                  onClick={() => setExportSize(false)}
                >
                  {t('sheet.size1x')}
                </button>
                <button
                  type="button"
                  className={big ? 'is-on' : ''}
                  aria-pressed={big}
                  onClick={() => setExportSize(true)}
                  title={t('sheet.size2xHint')}
                >
                  {t('sheet.size2x')}
                </button>
              </div>

              <span className="mono sheet-bar__count">
                {t('sheet.count', {
                  shown: String(shown.length).padStart(2, '0'),
                  total: String(entries.length).padStart(2, '0'),
                })}
              </span>
            </div>
          )}

          {entries.length > 0 && <KeepRates />}

          {loading ? (
            <Skeleton count={8} />
          ) : shown.length === 0 ? (
            <div className="empty">
              <p className="muted">
                {t(
                  entries.length === 0
                    ? 'sheet.empty'
                    : view === 'keep'
                      ? 'sheet.noKeepers'
                      : 'sheet.noUnjudged',
                )}
              </p>
              <p className="faint">{t(entries.length === 0 ? 'sheet.emptyHint' : 'sheet.filterHint')}</p>
            </div>
          ) : (
            <div className="stack stack--lg">
              {sheets.map((sheet) => {
                const circled = sheet.entries.filter((e) => e.shot.mark === 'keep').length;
                return (
                  <section key={sheet.name + sheet.date} className="plate-sheet">
                    <div className="plate-sheet__head">
                      <span className="plate-sheet__slug">
                        {sheet.name} · {date.long(sheet.date)}
                      </span>
                      {circled > 0 && (
                        <span className="plate-sheet__count">
                          {t(circled === 1 ? 'sheet.keeperCountOne' : 'sheet.keeperCount', {
                            n: circled,
                          })}
                        </span>
                      )}
                    </div>

                    <div className="plates">
                      {sheet.entries.map((entry) => {
                        frameNo += 1;
                        const label = povName(entry.shot.povId);
                        return (
                          <div
                            key={entry.shot.id}
                            className={`plate ${entry.shot.mark ? `is-${entry.shot.mark}` : ''}`}
                          >
                            {/*
                             * Pressing a photograph opens it, which is what pressing
                             * a photograph means everywhere else. Keeping and
                             * rejecting are two labelled buttons — the same pair the
                             * roll view and the client's review page already use —
                             * rather than a pencil stroke drawn over the picture.
                             */}
                            <div className="plate__shot">
                              <button
                                type="button"
                                className="plate__press"
                                aria-label={t('roll.inspect', { n: frameNo })}
                                onClick={() => setOpen(entry)}
                              >
                                {entry.asset && (
                                  <img
                                    className="plate__img"
                                    src={assetUrl(entry.asset.id)}
                                    alt=""
                                    loading="lazy"
                                  />
                                )}
                              </button>

                              <div className="plate__marks">
                                <MarkStrip
                                  mark={entry.shot.mark}
                                  onMark={(next) => void mark(entry.shot.id, next)}
                                  size="sm"
                                />
                              </div>
                            </div>

                            <div className="plate__foot">
                              <span className="plate__no">{String(frameNo).padStart(2, '0')}</span>
                              <span className="plate__pov">{label}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </>
      )}

      {open?.asset && (
        <Sheet
          title={povName(open.shot.povId)}
          subtitle={[open.shoot?.name, date.long(open.shot.createdAt)].filter(Boolean).join(' · ')}
          onClose={() => setOpen(undefined)}
          step={{
            index: (at < 0 ? 0 : at) + 1,
            total: shown.length,
            onPrev: () => stepTo(-1),
            onNext: () => stepTo(1),
          }}
          wide
        >
          {/*
           * The frame beside what there is to do with it. It used to be a
           * full-width image in a column, which on any normal screen meant the
           * photograph ran off the bottom and every control — the mark, the
           * download, the prompt it was made from — was below the fold of a
           * modal. You scrolled past the picture to reach the reason you
           * opened it.
           */}
          <div className="loupe">
            <div className="loupe__frame">
              <img className="loupe__img" src={assetUrl(open.asset.id)} alt="" />
            </div>

            <div className="stack">
              {/*
               * The decision first, and said in words. Two unlabelled boxes
               * floating at the top of the column read as debris; this is the
               * reason the panel is open, so it is a row that names itself.
               */}
              <div className="loupe__verdict">
                <span className="loupe__verdictLabel">{t('loupe.verdict')}</span>
                <MarkStrip
                  mark={open.shot.mark}
                  size="named"
                  onMark={(next) => void mark(open.shot.id, next)}
                />
                <span className="loupe__verdictNow">
                  {t(
                    open.shot.mark === 'keep'
                      ? 'loupe.kept'
                      : open.shot.mark === 'reject'
                        ? 'loupe.cut'
                        : 'loupe.undecided',
                  )}
                </span>
              </div>

              <a
                className="btn btn--ghost btn--block"
                href={downloadUrl(open.asset.id, big ? '2x' : undefined)}
                download
              >
                <DownloadIcon /> {t('common.download')}
              </a>
            </div>
          </div>
        </Sheet>
      )}
    </div>
  );
}

/**
 * What this studio keeps, as opposed to what it shoots.
 *
 * Folded away by default: it is a thing to consult when planning the next
 * roll, not something to read every time the sheet is opened. The cuts are
 * ordered best-first, and a set-up nobody has judged sorts below one that was
 * judged and rejected — an unknown is not evidence.
 */
function KeepRates() {
  const t = useT();
  const { data } = useLoad(() => api.insights(), { initial: undefined as Insights | undefined });
  if (!data || data.frames === 0) return null;

  const judged = data.kept + data.rejected;
  const cuts: Array<[string, InsightCut[]]> = [
    ['insight.bySetUp', data.bySetUp],
    ['insight.byGrade', data.byGrade],
    ['insight.byLight', data.byLight],
    ['insight.byScene', data.byScene],
  ];

  return (
    <details className="fold">
      <summary>{t('insight.title')}</summary>

      <div className="fold__body stack stack--sm">
        <p className="faint">
          {judged === 0
            ? t('insight.noneJudged', { n: data.frames })
            : t('insight.summary', {
                frames: data.frames,
                judged,
                kept: data.kept,
                downloads: data.downloads,
              })}
        </p>

        {cuts.map(([key, rows]) =>
          rows.length < 2 ? null : (
            <div key={key} className="stack stack--sm">
              <span className="label">{t(key)}</span>
              {rows.map((row) => (
                <div key={row.id} className="keeprate">
                  <span className="keeprate__name">{row.label}</span>
                  <span className="keeprate__bar">
                    <span
                      className="keeprate__fill"
                      style={{ width: `${Math.round((row.keepRate ?? 0) * 100)}%` }}
                    />
                  </span>
                  <span className="keeprate__value mono">
                    {row.keepRate === undefined ? '—' : `${Math.round(row.keepRate * 100)}%`}
                  </span>
                  <span className="keeprate__count mono">
                    {t('insight.frames', { n: row.frames })}
                    {row.downloads > 0 ? ` · ${t('insight.downloads', { n: row.downloads })}` : ''}
                  </span>
                </div>
              ))}
            </div>
          ),
        )}
      </div>
    </details>
  );
}
